/**
 * UpgradeBriefPicker — Strategic Intent Panel
 *
 * Redesigned priorities selector with:
 * - Icon-driven card grid for selection
 * - Drag-and-drop ranking with gold/silver/bronze styling
 * - Impact preview showing how choices affect scoring
 */

import React, { useCallback, useRef, useState } from 'react';

export interface PriorityOption {
  key: string;
  label: string;
  description: string;
  icon?: React.ReactNode;
}

export interface SelectedPriority {
  key: string;
  rank: number;
}

export interface UpgradeBriefPickerProps {
  options: PriorityOption[];
  value: SelectedPriority[];
  onChange: (value: SelectedPriority[]) => void;
  maxSelections?: number;
  className?: string;
  impactPreview?: Array<{ text: string; impact: 'positive' | 'negative' | 'neutral' }>;
}

const PRIORITY_ICONS: Record<string, string> = {
  low_upfront_cost: '\u00A3',
  low_running_cost: '\u21BB',
  hot_water_performance: '\u2668',
  reliability_simplicity: '\u2699',
  quiet_operation: '\u266B',
  future_proofing: '\u2192',
  minimal_disruption: '\u2714',
  smart_controls: '\u25C9',
  environmental_impact: '\u2618',
  comfort_even_heat: '\u2302',
};

const RANK_LABELS = ['1st priority', '2nd priority', '3rd priority'];

const UpgradeBriefPicker: React.FC<UpgradeBriefPickerProps> = ({
  options,
  value,
  onChange,
  maxSelections = 3,
  className = '',
  impactPreview,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);

  const normalizeRanks = (items: SelectedPriority[]) =>
    items.map((item, index) => ({ ...item, rank: index + 1 }));

  const sorted = [...value].sort((a, b) => a.rank - b.rank);
  const selectedKeys = new Set(sorted.map(s => s.key));

  const handleToggle = useCallback((key: string) => {
    if (selectedKeys.has(key)) {
      onChange(normalizeRanks(value.filter(p => p.key !== key)));
    } else {
      if (sorted.length >= maxSelections) return;
      onChange(normalizeRanks([...value, { key, rank: value.length + 1 }]));
    }
  }, [value, onChange, maxSelections, selectedKeys, sorted.length]);

  const handleDeselect = useCallback((key: string) => {
    onChange(normalizeRanks(value.filter(p => p.key !== key)));
  }, [value, onChange]);

  // Drag-and-drop handlers
  const handleDragStart = useCallback((index: number) => {
    dragItem.current = index;
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((targetIndex: number) => {
    if (dragItem.current === null || dragItem.current === targetIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newSorted = [...sorted];
    const [moved] = newSorted.splice(dragItem.current, 1);
    newSorted.splice(targetIndex, 0, moved);

    onChange(normalizeRanks(newSorted));
    setDragIndex(null);
    setDragOverIndex(null);
    dragItem.current = null;
  }, [sorted, onChange]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
    dragItem.current = null;
  }, []);

  const getOptionLabel = (key: string) =>
    options.find(o => o.key === key)?.label ?? key.replace(/_/g, ' ');

  const getOptionIcon = (key: string) => {
    const opt = options.find(o => o.key === key);
    if (opt?.icon) return opt.icon;
    return PRIORITY_ICONS[key] || '\u2605';
  };

  return (
    <div className={className}>
      {/* Step 1: Choose your focus */}
      <div className="upgrade-brief__step">
        <div className="upgrade-brief__step-header">
          <span className="upgrade-brief__step-number">1</span>
          <span className="upgrade-brief__step-label">Choose your focus (up to {maxSelections})</span>
        </div>

        <div className="upgrade-brief__card-grid">
          {options.map((option) => {
            const isSelected = selectedKeys.has(option.key);
            const isFull = sorted.length >= maxSelections && !isSelected;
            const rank = sorted.findIndex(s => s.key === option.key);

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => handleToggle(option.key)}
                disabled={isFull}
                className={[
                  'upgrade-brief__card',
                  isSelected && 'upgrade-brief__card--selected',
                  isFull && 'upgrade-brief__card--disabled',
                ].filter(Boolean).join(' ')}
              >
                <span className="upgrade-brief__card-icon">
                  {getOptionIcon(option.key)}
                </span>
                <div className="upgrade-brief__card-content">
                  <span className="upgrade-brief__card-label">{option.label}</span>
                  <span className="upgrade-brief__card-desc">{option.description}</span>
                </div>
                {isSelected && rank >= 0 && (
                  <span className={`upgrade-brief__card-rank upgrade-brief__card-rank--${rank + 1}`}>
                    {rank + 1}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 8, fontSize: '12px', color: '#6b7280' }}>
          {sorted.length} of {maxSelections} selected
        </div>
      </div>

      {/* Step 2: Rank your top selections */}
      {sorted.length > 0 && (
        <div className="upgrade-brief__step">
          <div className="upgrade-brief__step-header">
            <span className="upgrade-brief__step-number">2</span>
            <span className="upgrade-brief__step-label">Rank your priorities</span>
          </div>

          <div className="upgrade-brief__ranking">
            {sorted.map((selection, index) => (
              <div
                key={selection.key}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={[
                  'upgrade-brief__rank-slot',
                  dragIndex === index && 'upgrade-brief__rank-slot--dragging',
                  dragOverIndex === index && dragIndex !== index && 'upgrade-brief__rank-slot--dragover',
                ].filter(Boolean).join(' ')}
              >
                <span className={`upgrade-brief__rank-label upgrade-brief__rank-label--${index + 1}`}>
                  {RANK_LABELS[index] || `${index + 1}th`}
                </span>
                <span className="upgrade-brief__rank-icon">
                  {getOptionIcon(selection.key)}
                </span>
                <span className="upgrade-brief__rank-value">
                  {getOptionLabel(selection.key)}
                </span>
                <button
                  type="button"
                  className="upgrade-brief__rank-deselect"
                  onClick={(e) => { e.stopPropagation(); handleDeselect(selection.key); }}
                >
                  Remove
                </button>
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: Math.max(0, maxSelections - sorted.length) }).map((_, i) => (
              <div key={`empty-${i}`} className="upgrade-brief__rank-empty">
                <span className={`upgrade-brief__rank-label upgrade-brief__rank-label--${sorted.length + i + 1}`}>
                  {RANK_LABELS[sorted.length + i] || `${sorted.length + i + 1}th`}
                </span>
                <span style={{ color: '#9ca3af' }}>Select a priority above</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Impact preview */}
      {impactPreview && impactPreview.length > 0 && sorted.length > 0 && (
        <div className="upgrade-brief__step">
          <div className="upgrade-brief__step-header">
            <span className="upgrade-brief__step-number">3</span>
            <span className="upgrade-brief__step-label">How this shapes your recommendation</span>
          </div>

          <div className="upgrade-brief__impact">
            <div className="upgrade-brief__impact-title">Scoring impact</div>
            {impactPreview.map((item, i) => (
              <div key={i} className="upgrade-brief__impact-row">
                <span className={`upgrade-brief__impact-icon--${item.impact}`}>
                  {item.impact === 'positive' ? '\u2714' : item.impact === 'negative' ? '\u2718' : '\u2022'}
                </span>
                <span className="upgrade-brief__impact-text">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeBriefPicker;
